import { v4 as uuidv4 } from 'uuid';
import db_config from '../configs/db_config';
import { LANGUAGES } from '../consts';
import { ISeatInfo } from '../context/SystemContext/useSeat';
import { FoodItem, listRestaurantType } from '../interfaces/types';
import { db, storage } from '../libs/firebase';
import { getCurrentJstDate, getJstDateKey } from './jstDate';

export const isRestaurantOnline = (restaurant: listRestaurantType) => {
  const restaurant_open_state = restaurant.restaurant_open_state === db_config.RESTAURANT_OPEN_STATE_OPENED ? true : false;
  return restaurant_open_state;
};

export const getBrowserLocale = () => {
  const browserLanguage = window.navigator.language;

  if (['en', 'en-US'].includes(browserLanguage)) {
    return LANGUAGES.ENGLISH;
  }

  return LANGUAGES.JAPAN;
};

export const formatPriceItem = (price: number | string) => {
  const formattedPriceItem = price.toLocaleString('ja-JP');
  return formattedPriceItem;
};

const toFiniteNumber = (value: unknown): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

export const getActualItemQuantity = (food: Partial<FoodItem>): number => {
  const quantity = toFiniteNumber(food.item_quantity);
  const deletedQuantity = food.item_deleted_quantity !== undefined ? toFiniteNumber(food.item_deleted_quantity) : 0;
  return Math.max(quantity - deletedQuantity, 0);
};

export const calculateFoodsTotal = (foods?: Partial<FoodItem>[]): number => {
  if (!Array.isArray(foods)) return 0;
  return foods.reduce((total, food) => total + getActualItemQuantity(food) * toFiniteNumber(food.item_price), 0);
};

export const calculateOrderItemsTotal = (order?: { foods?: Partial<FoodItem>[]; promotion_foods?: Partial<FoodItem>[] }): number => {
  if (!order) return 0;
  return calculateFoodsTotal(order.foods) + calculateFoodsTotal(order.promotion_foods);
};

export const calculateOrderItemQuantity = (order?: { foods?: Partial<FoodItem>[]; promotion_foods?: Partial<FoodItem>[] }): number => {
  if (!order) return 0;
  return [...(order.foods ?? []), ...(order.promotion_foods ?? [])].reduce((total, food) => total + getActualItemQuantity(food), 0);
};

export const getUTCDateStr = (date?: Date) => {
  const dateKey = date ? getJstDateKey(date) : getJstDateKey(getCurrentJstDate());
  return dateKey.replace(/-/g, '');
};

export const getTermsAndConditions = async (locale: string, stadium_id: string) => {
  try {
    const fetchTextFromFile = async (path: string) => {
      try {
        const storageRef = storage.ref(path);
        const url = await storageRef.getDownloadURL();
        const response = await fetch(url);
        return await response.text();
      } catch (error: any) {
        if (error.code === 'storage/object-not-found') {
          return null;
        }
        throw error;
      }
    };

    let file_path = `terms_and_coditions/${stadium_id}_${locale}.txt`;
    let content = await fetchTextFromFile(file_path);

    if (!content) {
      file_path = `terms_and_coditions/${stadium_id}.txt`;
      content = await fetchTextFromFile(file_path);
    }

    return content;
  } catch (error) {
    return '';
  }
};

const updateLatestIdInDb = async (stadium_id: string, menyOrderId: number, dateStr: string) => {
  const stadiumRef = db.collection(db_config.DB_TABLE_STADIUM_MANAGE).doc(stadium_id);
  const stadiumDoc = await stadiumRef.get();
  const data = stadiumDoc.data();

  const latest_ids = data?.latest_ids || {};

  // Clean up old dates, only keep today and future dates
  const todayStr = getUTCDateStr();
  const cleanedLatestIds: { [key: string]: number } = {};

  Object.keys(latest_ids).forEach((key) => {
    if (key >= todayStr) {
      cleanedLatestIds[key] = latest_ids[key];
    }
  });

  cleanedLatestIds[dateStr] = menyOrderId;

  await stadiumRef.update({
    latest_ids: cleanedLatestIds,
    last_update_date: dateStr,
  });
};

export const generateOrderId = async (seatInfo: ISeatInfo, _stadium_id?: string) => {
  const dateStr = getUTCDateStr(seatInfo?.order_date ? new Date(seatInfo.order_date) : undefined);
  const stadium_id = _stadium_id || sessionStorage.getItem('stadiumID');
  if (!stadium_id) {
    const randomId = uuidv4().slice(0, 8);
    return `${seatInfo.id}-${randomId}`;
  }

  const orderRef = await db.collection(db_config.DB_TABLE_STADIUM_MANAGE).doc(stadium_id).get();
  const data = orderRef.data();

  const latest_ids = data?.latest_ids || {};
  let latest_id = latest_ids[dateStr] ?? 0;

  let menyOrderId = latest_id + 1;

  if (menyOrderId > 9999) {
    menyOrderId = 1;
  }
  const menyOrderIdStr = menyOrderId.toString().padStart(4, '0');

  await updateLatestIdInDb(stadium_id, menyOrderId, dateStr);

  return `${dateStr}-${seatInfo.id}-${menyOrderIdStr}`;
};

interface StorageItemWithExpiry {
  value: string;
  expiredAt: number;
}

export const setItemWithExpiry = (key: string, value: string, expiryInDays: number = 60): void => {
  const now = new Date();
  const item: StorageItemWithExpiry = {
    value: value,
    expiredAt: now.getTime() + expiryInDays * 24 * 60 * 60 * 1000,
  };
  localStorage.setItem(key, JSON.stringify(item));
};

export const getItemWithExpiry = (key: string): string | null => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) {
    return null;
  }

  try {
    const item: StorageItemWithExpiry = JSON.parse(itemStr);
    const now = new Date();

    if (now.getTime() > item.expiredAt) {
      localStorage.removeItem(key);
      return null;
    }

    return item.value;
  } catch (error) {
    return itemStr;
  }
};
